import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Filter,
  ArrowUpNarrowWide,
  ArrowDownWideNarrow,
  Eye,
  Upload,
  UploadCloud,
  X,
  FileText,
  ChevronLeft,
  ChevronRight,
  FilePenLine,
  Truck,
  Download,
  Package,
  Search,
  Check,
  Ruler,
  Weight,
} from "lucide-react";
import {
  getAllShipmentDetail,
  uploadShipmentDoc,
  getInvoiceDetails,
  getShipmentBYItemId,
  updateShipment,
  downloadShipmentDoc,
  createShipment,
} from "../../reducers/shipment.slice";
import { getOrderItemListApproved } from "../../reducers/order.slice";
import { uploadPayoutInvoice } from "../../reducers/payouts.slice";
import {
  convertDateFormatInUtc,
  downloadDocument,
  downloadImagesAsZip,
  countActiveFiltersAndSort,
  getDeadlineInfo,
  getShipmentDeadlineInfo,
  subtractHoursUtc,
} from "../../utils/util";
import { generatePDF } from "../../utils/generateInvoice";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import StatCard from "../../components/shared/StatCard";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import DataTable from "../../components/shared/DataTable";
import QuantityStepper from "../../components/shared/QuantityStepper";
import PolicyDeadlineBadge from "../../components/shared/PolicyDeadlineBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import ShipmentBoxesReview from "./ShipmentBoxesReview";
import ShipmentRulesReview from "./ShipmentRulesReview";
import { SHIPMENT_BOX_LIMIT_HELPER, getBoxRuleErrors, getShipmentEstimatedValue, hasBoxRuleErrors } from "./shipmentRules";

// The four quick-filter tabs (matching the artifact) cover the statuses a
// member checks most often. The "Filter" dropdown covers what's left over
// (PENDING_SHIPMENT/DELAYED/CANCELLED) plus type + sort.
const TAB_OPTIONS = [
  { key: "all", label: "All", statuses: [] },
  { key: "label", label: "Label pending", statuses: ["LABEL_PENDING"] },
  { key: "transit", label: "In transit", statuses: ["SHIPPED", "IN_TRANSIT"] },
  { key: "received", label: "Received", statuses: ["RECEIVED"] },
];
const STATUS_OPTIONS = ["PENDING_SHIPMENT", "DELAYED", "CANCELLED"];
const TYPE_OPTIONS = ["VENDOR", "SHIP_DIRECT"];
const SHIPMENT_DEADLINE_TITLE =
  "Ship within 48h of receiving labels. Shipments not scanned in time may be cancelled.";

// Caps the sample used to sum lbs weight for the strip (no aggregate
// endpoint exists) — the three status counts are exact (fetched via total).
const WEIGHT_SAMPLE_LIMIT = 200;

// RSC's own tracking page on the Partners Portal. It only knows labels bought
// there (Label Desk / RSC Ship); any other number shows "not found".
const PARTNERS_TRACKING_URL = "https://partners.rs-chain.com/track";

const MemberShippingAndTrackingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const getStoredStatuses = sessionStorage.getItem("shipTrackingMember_selectedStatuses");
    return getStoredStatuses ? JSON.parse(getStoredStatuses) : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const getSelectedTypes = sessionStorage.getItem("shipTrackingMember_selectedTypes");
    return getSelectedTypes ? JSON.parse(getSelectedTypes) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("shipTrackingMember_sortDirection") || null;
  });
  const [activeTab, setActiveTab] = useState(() => {
    const stored = sessionStorage.getItem("shipTrackingMember_selectedStatuses");
    const storedStatuses = stored ? JSON.parse(stored) : [];
    const match = TAB_OPTIONS.find(
      (tab) =>
        tab.statuses.length === storedStatuses.length &&
        tab.statuses.every((s) => storedStatuses.includes(s))
    );
    return match ? match.key : null;
  });
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const getRowsPerPage = sessionStorage.getItem("shipTrackingMember_rowsPerPage");
    return getRowsPerPage ? JSON.parse(getRowsPerPage) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const [filterOpen, setFilterOpen] = useState(false);
  const [shipmentStats, setShipmentStats] = useState(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadShipmentId, setUploadShipmentId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  // View Shipment dialog — ports ViewShipmentPage's member-facing content
  // (the file is 2,322 lines with real admin/manager-only branches for WMS
  // sync, Edit Status, Upload Label; none of that is built here since this
  // whole page only ever renders for MEMBER).
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewShipmentData, setViewShipmentData] = useState(null);
  const [viewBoxPage, setViewBoxPage] = useState(1);
  const BOXES_PER_PAGE = 4;

  // SHIP_DIRECT-only per-item quantity editing (PENDING_SHIPMENT status),
  // same restriction the original enforces.
  const [editedQuantities, setEditedQuantities] = useState({});
  const [qtyEditOpen, setQtyEditOpen] = useState(false);
  const [qtyEditTarget, setQtyEditTarget] = useState(null);
  const [qtyEditValue, setQtyEditValue] = useState("");
  const [qtyEditError, setQtyEditError] = useState("");

  // Bulk Tracking Import (member + PENDING_SHIPMENT) — marks the shipment
  // SHIPPED. No artifact equivalent; real functionality, ported as-is.
  const SHIP_TO_ADDRESSES = [
    "23831 Via Fabricante, Suite 302, Mission Viejo, CA 92691",
    "RSC, 201 Carson Drive, Bear, DE 19701",
  ];
  const [shipToAddress, setShipToAddress] = useState("");
  const [trackingInput, setTrackingInput] = useState("");
  const [trackingInputError, setTrackingInputError] = useState("");
  const [savingShipment, setSavingShipment] = useState(false);

  const [downloadingDoc, setDownloadingDoc] = useState(null);

  // Create Shipment wizard — ports CreateShipmentPage's box-count ->
  // per-box (items+dimensions) -> submit flow (1,318 lines, also left
  // completely untouched) exactly, but splits each box's single combined
  // screen into two focused steps (Select Items / Dimensions) and adds a
  // Review step before submit — the artifact does the same items+dims
  // combine the real page does, and the real page has no review step at
  // all; both are worth improving on rather than copying faithfully.
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [totalBox, setTotalBox] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [boxPhase, setBoxPhase] = useState("items");
  const [createFormData, setCreateFormData] = useState([]);
  const [upcOptions, setUpcOptions] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [replicateBoxes, setReplicateBoxes] = useState(false);
  const [numberOfBoxesToReplicate, setNumberOfBoxesToReplicate] = useState(1);
  const [rulesAcknowledged, setRulesAcknowledged] = useState(false);

  const CHUNK_SIZE = 50;
  const isInitialRender = useRef(true);

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  // Block accidental refresh/close while a receipt upload chain
  // (doc -> invoice -> payout invoice) is in flight, same guard the
  // original standalone upload page had.
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (uploading) {
        event.preventDefault();
        event.returnValue = "Upload is still processing. Please wait.";
      }
    };
    const blockRefresh = (event) => {
      if (uploading && (event.key === "F5" || (event.ctrlKey && event.key === "r"))) {
        event.preventDefault();
        showToast("Upload is still processing. Please wait.", "warning");
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", blockRefresh);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", blockRefresh);
    };
  }, [uploading]);

  const openUploadDialog = (shipmentId) => {
    setUploadShipmentId(shipmentId);
    setUploadedFiles([]);
    setUploadDialogOpen(true);
  };

  const handleUploadDialogChange = (open) => {
    if (uploading) return;
    setUploadDialogOpen(open);
    if (!open) {
      setUploadShipmentId(null);
      setUploadedFiles([]);
    }
  };

  const addFiles = (files) => setUploadedFiles((prev) => [...prev, ...files]);
  const handleRemoveFile = (index) => setUploadedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleUploadDrop = (e) => {
    e.preventDefault();
    setUploadDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) addFiles(files);
  };

  const handleUploadFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) addFiles(files);
    e.target.value = "";
  };

  const handleSubmitReceipt = () => {
    setUploading(true);
    const docFormData = new FormData();
    uploadedFiles.forEach((file) => docFormData.append("files", file));
    const payload = { itemId: uploadShipmentId, docType: "RECEIPT" };

    dispatch(uploadShipmentDoc({ payload, docFormData })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setUploading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (!res?.payload?.generate_invoice) {
        setUploading(false);
        setUploadDialogOpen(false);
        showToast("Receipt uploaded successfully", "success");
        handleRefresh();
        return;
      }

      dispatch(getInvoiceDetails({ id: uploadShipmentId })).then((response) => {
        if (response?.error && Object.keys(response?.error)?.length > 0) {
          setUploading(false);
          showToast(response?.error?.message, "error");
          return;
        }
        const formdata = generatePDF(response?.payload);
        const uploadPayload = {
          id: res?.payload?.payout_id,
          document_type: "INVOICE",
          invoice_number: response?.payload?.invoice_number,
        };

        dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then((uploadResponse) => {
          setUploading(false);
          if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
            showToast(uploadResponse?.error?.message, "error");
            return;
          }
          setUploadDialogOpen(false);
          showToast("Receipt uploaded successfully", "success");
          handleRefresh();
        });
      });
    });
  };

  const formatAddress = (addressObj) => {
    if (!addressObj) return "";
    const parts = [
      addressObj.address,
      addressObj.building,
      [addressObj.city, addressObj.state, addressObj.zipcode].filter(Boolean).join(", "),
    ].filter(Boolean);
    return parts.join(" , ");
  };

  const openViewDialog = (shipmentId) => {
    setViewDialogOpen(true);
    setViewLoading(true);
    setViewBoxPage(1);
    setEditedQuantities({});
    setShipToAddress("");
    setTrackingInput("");
    setTrackingInputError("");

    dispatch(getShipmentBYItemId(shipmentId)).then((res) => {
      setViewLoading(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      const member_name = `${res?.payload?.user_details?.first_name || ""} ${res?.payload?.user_details?.last_name || ""}`.trim();
      setViewShipmentData({ ...res?.payload, member_name });
    });
  };

  const closeViewDialog = () => {
    if (savingShipment) return;
    setViewDialogOpen(false);
    setViewShipmentData(null);
  };

  const openQtyEdit = (item, boxId) => {
    setQtyEditTarget({ ...item, boxId });
    const key = `${boxId}-${item.index}`;
    setQtyEditValue(String(editedQuantities[key] ?? item.quantity));
    setQtyEditError("");
    setQtyEditOpen(true);
  };

  const handleQtyEditChange = (value) => {
    setQtyEditValue(value);
    const numValue = parseInt(value, 10);
    if (value === "") {
      setQtyEditError("Quantity is required");
    } else if (isNaN(numValue) || numValue <= 0 || !Number.isInteger(numValue)) {
      setQtyEditError("Please enter a valid number");
    } else if (qtyEditTarget && numValue > qtyEditTarget.approved_quantity) {
      setQtyEditError(`Quantity cannot exceed approved quantity ${qtyEditTarget.approved_quantity}`);
    } else {
      setQtyEditError("");
    }
  };

  const confirmQtyEdit = () => {
    if (!qtyEditTarget || qtyEditError || qtyEditValue === "") return;
    const numValue = parseInt(qtyEditValue, 10);
    const key = `${qtyEditTarget.boxId}-${qtyEditTarget.index}`;

    setEditedQuantities((prev) => ({ ...prev, [key]: numValue }));
    setViewShipmentData((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const boxIndex = updated.box_details.findIndex((box) => box.id === qtyEditTarget.boxId);
      if (boxIndex !== -1) {
        updated.box_details[boxIndex].box_items[qtyEditTarget.index].quantity = numValue;
      }
      return updated;
    });
    setQtyEditOpen(false);
  };

  const resetEditedQuantities = () => {
    setEditedQuantities({});
    showToast("All quantity changes have been discarded", "info");
    openViewDialog(viewShipmentData?.id);
  };

  const handleSaveShipment = () => {
    const rawTrackingNumbers = trackingInput
      .split(/[,\n\s]+/)
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
    const uniqueTrackingNumbers = [...new Set(rawTrackingNumbers)];

    const trackingFrequency = {};
    rawTrackingNumbers.forEach((num) => {
      trackingFrequency[num] = (trackingFrequency[num] || 0) + 1;
    });
    const duplicates = Object.keys(trackingFrequency).filter((num) => trackingFrequency[num] > 1);
    if (duplicates.length > 0) {
      const duplicatesList =
        duplicates.length <= 3
          ? duplicates.join(", ")
          : `${duplicates.slice(0, 3).join(", ")} and ${duplicates.length - 3} more`;
      setTrackingInputError(`Removed duplicate tracking numbers: ${duplicatesList}`);
    }

    if (uniqueTrackingNumbers.length === 0) {
      setTrackingInputError("Please enter at least one tracking number.");
      return;
    }
    if (!shipToAddress) return;

    setSavingShipment(true);
    const boxItems = viewShipmentData?.box_details?.[0]?.box_items || [];
    const payload = {
      id: viewShipmentData.id,
      shipment_type: viewShipmentData.shipment_type,
      status: "SHIPPED",
      box_items: boxItems,
      tracking_number: uniqueTrackingNumbers,
      ship_to_address: shipToAddress,
    };

    dispatch(updateShipment(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSavingShipment(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (!res?.payload?.generate_invoice) {
        setSavingShipment(false);
        setViewDialogOpen(false);
        handleRefresh();
        return;
      }
      dispatch(getInvoiceDetails({ id: viewShipmentData.id })).then((response) => {
        if (response?.error && Object.keys(response?.error)?.length > 0) {
          setSavingShipment(false);
          showToast(response?.error?.message, "error");
          return;
        }
        const formdata = generatePDF(response?.payload);
        const uploadPayload = {
          id: res?.payload?.payout_id,
          document_type: "INVOICE",
          invoice_number: response?.payload?.invoice_number,
        };
        dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then((uploadResponse) => {
          setSavingShipment(false);
          if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
            showToast(uploadResponse?.error?.message, "error");
            return;
          }
          setViewDialogOpen(false);
          showToast("Shipment marked as shipped", "success");
          handleRefresh();
        });
      });
    });
  };

  const handleDownloadDoc = (id, action) => {
    setDownloadingDoc(action);
    dispatch(downloadShipmentDoc({ id, docType: action })).then((res) => {
      setDownloadingDoc(null);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload?.length === 1) {
        const item = res.payload[0];
        downloadDocument(item?.document_bytes, action, item?.content_type);
      } else if (res?.payload?.length > 1) {
        downloadImagesAsZip(res.payload, action);
      }
      showToast("File downloaded successfully", "success");
    });
  };

  // tracking_number is stored as "master, box2, box3"; the master (first)
  // number covers every box on the partners tracking page.
  const handleViewTracking = () => {
    const masterTracking = (viewShipmentData.tracking_number || "").split(",")[0].replace(/[\s-]/g, "");
    window.open(`${PARTNERS_TRACKING_URL}/${masterTracking}`, "_blank", "noopener,noreferrer");
  };

  const openCreateDialog = () => {
    setCreateDialogOpen(true);
    setCreateLoading(true);
    setTotalBox(null);
    setActiveStep(0);
    setBoxPhase("items");
    setCreateFormData([]);
    setItemSearchTerm("");
    setReplicateBoxes(false);
    setNumberOfBoxesToReplicate(1);
    setRulesAcknowledged(false);

    dispatch(getOrderItemListApproved()).then((res) => {
      setCreateLoading(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedOptions = res?.payload?.map((item) => {
        const approvalBaseDate =
          item?.order_status_updated_at || subtractHoursUtc(item?.order_expiry);
        const priceLockDeadlineInfo = getDeadlineInfo(approvalBaseDate);

        return {
          upc: item?.upc,
          quantity: item?.quantity,
          item_name: item?.item_name,
          order_id: item?.order_id,
          item_id: item?.id,
          buy_price: item?.buy_price || 0,
          cashout_price: item?.cashout_price,
          allowedQty: item?.quantity - item?.shipped_quantity,
          originalAllowedQty: item?.quantity - item?.shipped_quantity,
          approvalDate: approvalBaseDate
            ? convertDateFormatInUtc(approvalBaseDate, "MMM D, YYYY · h:mm A")
            : "—",
          createShipmentBy: priceLockDeadlineInfo
            ? convertDateFormatInUtc(priceLockDeadlineInfo.deadline, "MMM D, YYYY · h:mm A")
            : "—",
          priceLockDeadlineInfo,
        };
      });
      setUpcOptions(formattedOptions || []);
    });
  };

  const closeCreateDialog = () => {
    if (createSubmitting) return;
    setCreateDialogOpen(false);
  };

  const isBoxComplete = (stepIndex) => {
    const box = createFormData[stepIndex];
    if (!box) return false;
    const dimensionsComplete = Object.values(box.box_dimension).every((v) => v !== "" && v !== null && v !== "0" && v !== "0.0");
    const itemsComplete = box.box_items.length > 0 && box.box_items.every((item) => item.quantity > 0);
    return dimensionsComplete && itemsComplete;
  };

  const getIncompleteBoxStep = () => {
    const boxCount = Number(totalBox) || 0;
    for (let i = 0; i < boxCount; i += 1) {
      const boxData = createFormData[i];
      if (!boxData || boxData.box_items.length === 0 || boxData.box_items.some((item) => !(item.quantity > 0))) {
        return { step: i + 1, phase: "items" };
      }
      const dimensionsComplete = Object.values(boxData.box_dimension).every((v) => v !== "" && v !== null && v !== "0" && v !== "0.0");
      if (!dimensionsComplete) {
        return { step: i + 1, phase: "dimensions" };
      }
    }
    return null;
  };

  const advanceAfterCurrentBox = () => {
    if (activeStep < totalBox) {
      setActiveStep((prev) => prev + 1);
      setBoxPhase("items");
      setItemSearchTerm("");
      return;
    }

    const incompleteBox = getIncompleteBoxStep();
    if (incompleteBox) {
      setActiveStep(incompleteBox.step);
      setBoxPhase(incompleteBox.phase);
      setItemSearchTerm("");
      return;
    }

    setActiveStep(totalBox + 1);
  };

  const handleCreateTotalBoxChange = (numValue) => {
    setRulesAcknowledged(false);
    setTotalBox(numValue);
    setCreateFormData((prev) => {
      const newData = [...prev];
      while (newData.length > numValue) newData.pop();
      while (newData.length < numValue) {
        newData.push({
          box_items: [],
          box_dimension: { height: "", width: "", length: "", weight: "" },
          dimensions_copied: false,
        });
      }
      return newData;
    });
  };

  const handleCreateAddBox = () => {
    setRulesAcknowledged(false);
    const newTotal = totalBox === null || totalBox === 0 || !totalBox ? 1 : parseInt(totalBox) + 1;
    setTotalBox(newTotal);
    setCreateFormData((prev) => [
      ...prev,
      { box_items: [], box_dimension: { height: "", width: "", length: "", weight: "" }, dimensions_copied: false },
    ]);
  };

  const handleCreateReduceBox = () => {
    if (totalBox > 0) {
      setRulesAcknowledged(false);
      setTotalBox(parseInt(totalBox) - 1);
      setCreateFormData((prev) => prev.slice(0, -1));
    }
  };

  const handleCreateFormDataChange = (index, field, value) => {
    setRulesAcknowledged(false);
    const removeLeadingZeros = (val) => {
      if (val === null || val === "") return val;
      if (typeof val === "string" && /^0(\.\d+)?$/.test(val)) return val;
      if (typeof val === "string") return val.replace(/^0+(?!\.)/, "");
      return val;
    };

    setCreateFormData((prev) => {
      const updated = [...prev];
      if (field === "box_items") {
        updated[index].box_items = value.map((item_id) => {
          const itemDetails = upcOptions.find((option) => option.item_id === item_id);
          return {
            ...itemDetails,
            quantity: updated[index].box_items.find((item) => item.item_id === item_id)?.quantity || 1,
          };
        });
      } else if (field in updated[index].box_dimension) {
        updated[index].box_dimension[field] = removeLeadingZeros(value);
        updated[index].dimensions_copied = false;
      }
      updated[index].box_name = `Box ${index + 1}`;
      return updated;
    });
  };

  const toggleItemSelection = (boxIndex, itemId) => {
    const current = createFormData[boxIndex]?.box_items.map((item) => item.item_id) || [];
    const next = current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId];
    handleCreateFormDataChange(boxIndex, "box_items", next);
  };

  const handleCreateQuantityChange = (boxIndex, item_id, quantity) => {
    setRulesAcknowledged(false);
    const numericQuantity = Number(quantity) || 0;
    setCreateFormData((prev) => {
      const updated = [...prev];
      const item = updated[boxIndex].box_items.find((item) => item.item_id === item_id);
      if (item) item.quantity = numericQuantity;
      return updated;
    });
    setUpcOptions((prev) =>
      prev.map((item) => {
        if (item.item_id === item_id) {
          const totalUsedExceptCurrent =
            Object.values(createFormData)
              .flatMap((box) => box.box_items)
              .filter((boxItem) => boxItem.item_id === item_id)
              .reduce((sum, boxItem) => sum + (Number(boxItem.quantity) || 0), 0) - numericQuantity;
          return { ...item, allowedQty: item.originalAllowedQty - totalUsedExceptCurrent };
        }
        return item;
      })
    );
  };

  const getRemainingAllowedQty = (item_id, currentBoxIndex) => {
    const option = upcOptions.find((item) => item.item_id === item_id);
    if (!option) return 0;
    const totalUsedQuantity = createFormData.reduce((sum, box, boxIndex) => {
      if (boxIndex === currentBoxIndex) return sum;
      const boxItem = box.box_items.find((item) => item.item_id === item_id);
      return sum + (Number(boxItem?.quantity) || 0);
    }, 0);
    const remainingQty = option.originalAllowedQty - totalUsedQuantity;
    return remainingQty > 0 ? remainingQty : 0;
  };

  const handleReplicateBoxCountChange = (value) => {
    const maxBoxes = Math.max(totalBox - activeStep, 1);
    const numValue = Number(value) || 1;
    setNumberOfBoxesToReplicate(Math.max(1, Math.min(maxBoxes, numValue)));
  };

  const validateBoxes = (boxDetails) => {
    const itemTracker = new Map();
    for (const box of boxDetails) {
      for (const item of box.box_items) {
        const key = `${item.item_id}_${item.upc}`;
        const existing = itemTracker.get(key) || { totalQty: 0, originalAllowedQty: item.originalAllowedQty };
        existing.totalQty += item.quantity;
        existing.originalAllowedQty = Math.min(existing.originalAllowedQty, item.originalAllowedQty);
        itemTracker.set(key, existing);
      }
    }
    for (const [, { totalQty, originalAllowedQty }] of itemTracker) {
      if (totalQty > originalAllowedQty) return { isValid: false };
    }
    return { isValid: true };
  };

  const handleWizardNext = () => {
    if (activeStep === 0) {
      if (!totalBox) return;
      setActiveStep(1);
      setBoxPhase("items");
      setItemSearchTerm("");
      return;
    }

    if (boxPhase === "items") {
      if (createFormData[activeStep - 1]?.dimensions_copied && isBoxComplete(activeStep - 1)) {
        advanceAfterCurrentBox();
        return;
      }
      setBoxPhase("dimensions");
      return;
    }

    // boxPhase === "dimensions": same recompute + validation the original
    // handleNext ran before advancing or replicating. The original page's
    // activeStep was a 0-indexed array index, so `boxIndex >= activeStep`
    // correctly excluded the box being validated. This page's activeStep is
    // the 1-indexed "Box N of N" the user sees, so the current box lives at
    // boxIndex === activeStep - 1 — excluding by `>= activeStep` (unchanged
    // from the original) also excludes nothing and instead counts the
    // current box against itself, subtracting its own quantity from the
    // allowed amount twice and false-positiving "Quantities exceed
    // available amounts" on essentially every submit.
    const updatedUpcOptions = upcOptions.map((option) => {
      const totalUsedQuantity = createFormData.reduce((sum, box, boxIndex) => {
        if (boxIndex >= activeStep - 1) return sum;
        const boxItem = box.box_items.find((item) => item.item_id === option.item_id);
        return sum + (boxItem?.quantity || 0);
      }, 0);
      return { ...option, allowedQty: option.originalAllowedQty - totalUsedQuantity };
    });
    setUpcOptions(updatedUpcOptions);

    const hasExceededQuantities = createFormData[activeStep - 1]?.box_items.some((item) => {
      const updatedOption = updatedUpcOptions.find((opt) => opt.item_id === item.item_id);
      return item.quantity > (updatedOption?.allowedQty || 0);
    });
    if (hasExceededQuantities) {
      showToast("Quantities exceed available amounts", "error");
      return;
    }

    if (replicateBoxes && numberOfBoxesToReplicate > 0) {
      const currentBoxData = createFormData[activeStep - 1];
      const boxesToApply = parseInt(numberOfBoxesToReplicate, 10);

      setCreateFormData((prev) => {
        const updated = [...prev];
        for (let i = 0; i < boxesToApply; i++) {
          const newIndex = activeStep + i;
          if (!updated[newIndex]) continue;
          updated[newIndex] = { ...updated[newIndex], box_dimension: { ...currentBoxData.box_dimension }, dimensions_copied: true };
        }
        return updated;
      });

      setReplicateBoxes(false);
      setNumberOfBoxesToReplicate(1);
    }

    advanceAfterCurrentBox();
  };

  const handleWizardBack = () => {
    if (activeStep === totalBox + 1) {
      setRulesAcknowledged(false);
      setActiveStep(totalBox);
      setBoxPhase("dimensions");
      return;
    }
    if (boxPhase === "dimensions") {
      setRulesAcknowledged(false);
      setBoxPhase("items");
      return;
    }
    if (activeStep === 1) {
      setActiveStep(0);
    } else {
      setActiveStep((prev) => prev - 1);
      setBoxPhase("dimensions");
    }
  };

  const handleCreateSubmit = () => {
    const payload = { status: "LABEL_PENDING", box_details: createFormData };
    if (payload.box_details.some((box) => hasBoxRuleErrors(box.box_dimension))) {
      showToast("Please fix box weight and dimension limits before creating this shipment.", "error");
      return;
    }
    if (!rulesAcknowledged) {
      showToast("Please confirm the shipment rules before creating this shipment.", "error");
      return;
    }
    const result = validateBoxes(payload.box_details);
    if (!result.isValid) {
      showToast("Quantities exceed approved limits — please review your boxes", "error");
      return;
    }
    setCreateSubmitting(true);
    dispatch(createShipment(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setCreateSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setCreateSubmitting(false);
      setCreateDialogOpen(false);
      showToast("Shipment created successfully", "success");
      handleRefresh();
    });
  };

  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();

    Promise.all([
      dispatch(getAllShipmentDetail({ skip: 0, limit: 1, shipment_status: ["LABEL_PENDING"], shipment_type: [] })),
      dispatch(getAllShipmentDetail({ skip: 0, limit: 1, shipment_status: ["IN_TRANSIT"], shipment_type: [] })),
      dispatch(
        getAllShipmentDetail({
          skip: 0,
          limit: WEIGHT_SAMPLE_LIMIT,
          shipment_status: ["RECEIVED"],
          shipment_type: [],
        })
      ),
    ]).then(([labelRes, transitRes, receivedRes]) => {
      const receivedShipments = receivedRes?.payload?.data || [];
      const receivedThisYear = receivedShipments.filter(
        (s) => s?.shipment_date && new Date(s.shipment_date).getFullYear() === currentYear
      ).length;
      const lbsWeight = receivedShipments
        .filter((s) => (s?.total_weight_unit || "").toLowerCase().startsWith("lb"))
        .reduce((sum, s) => sum + (s?.total_weight_value || 0), 0);

      setShipmentStats({
        needsLabel: labelRes?.payload?.total ?? 0,
        inTransit: transitRes?.payload?.total ?? 0,
        receivedThisYear,
        lbsWeight,
      });
    });
  }, [dispatch]);

  const loadDataChunk = async (chunkNumber = 0, overrides = {}) => {
    // overrides lets a tab click apply immediately without waiting for the
    // setSelectedStatuses state update to land (avoids a stale-closure read
    // on the very first fetch after the click).
    const effectiveStatuses = overrides.statuses ?? selectedStatuses;
    const effectiveTypes = overrides.types ?? selectedTypes;

    const filterKey = JSON.stringify({
      selectedStatuses: [...effectiveStatuses].sort(),
      selectedTypes: [...effectiveTypes].sort(),
      search: searchTerm,
      sortDirection: sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    if (!overrides.skipCache && pageCache.has(cacheKey)) {
      const cachedData = pageCache.get(cacheKey);
      if (cachedData.totalRows !== undefined) {
        setTotalRows(cachedData.totalRows);
      }
      return cachedData.data;
    }

    if (loadingChunk) {
      return [];
    }

    setLoadingChunk(true);

    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
        shipment_status: [],
        shipment_type: [],
      };

      if (effectiveStatuses && effectiveStatuses.length > 0) {
        payload.shipment_status = effectiveStatuses;
      }
      if (effectiveTypes && effectiveTypes.length > 0) {
        payload.shipment_type = effectiveTypes;
      }
      if (searchTerm && searchTerm?.trim() !== "") {
        payload.search = searchTerm?.trim();
      }
      if (sortDirection && sortDirection !== "null") {
        payload.sort_by = "TOTAL_BOXES";
        payload.sort_order = sortDirection;
      }

      const res = await dispatch(getAllShipmentDetail(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setHasMoreData(false);
        setFilteredData([]);
        showToast(res?.error?.message, "error");
        return [];
      }

      if (!res?.payload?.data || !Array.isArray(res.payload.data)) {
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }

      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) {
        setTotalRows(res.payload.total);
      }

      const formattedShipments = res?.payload?.data?.map((item) => {
        const shipmentDate = item?.shipment_date
          ? convertDateFormatInUtc(item?.shipment_date, "MMM D, YYYY")
          : "-";
        const shipDeadlineInfo = getShipmentDeadlineInfo(item);
        const member_name = `${item?.user_details?.first_name || ""} ${item?.user_details?.last_name || ""}`.trim();
        return {
          ...item,
          shipmentDate,
          labelUploadedDate: item?.label_upload_date
            ? convertDateFormatInUtc(item.label_upload_date, "MMM D, YYYY · h:mm A")
            : "—",
          shipByDate: shipDeadlineInfo
            ? convertDateFormatInUtc(shipDeadlineInfo.deadline, "MMM D, YYYY · h:mm A")
            : "—",
          shipDeadlineInfo,
          member_name,
        };
      });

      setPageCache(
        (prev) =>
          new Map(
            prev.set(cacheKey, {
              data: formattedShipments,
              timestamp: Date.now(),
              totalRows: res?.payload?.total,
            })
          )
      );

      const hasMore = formattedShipments?.length === CHUNK_SIZE;
      setHasMoreData(hasMore);
      return formattedShipments;
    } catch (error) {
      console.error("Error in loadDataChunk:", error);
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
    }
  };

  const handleViewShipment = (row) => {
    openViewDialog(row?.id);
  };

  const columns = [
    {
      key: "id",
      label: "Shipment ID",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.id}</span>,
    },
    {
      key: "shipment_type",
      label: "Type",
      render: (row) => row?.shipment_type?.replace(/_/g, " "),
    },
    { key: "total_boxes", label: "Boxes" },
    {
      key: "total_weight_unit",
      label: "Weight",
      render: (row) =>
        row?.total_weight_value ? `${row.total_weight_value} ${row.total_weight_unit || ""}` : "-",
    },
    { key: "total_items", label: "SKU" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "shipByDate",
      label: "Shipping Deadline",
      render: (row) =>
        row.shipDeadlineInfo ? (
          <span
            title={SHIPMENT_DEADLINE_TITLE}
            style={{
              color: row.shipDeadlineInfo.isPastDue ? "#f27059" : "#fff",
              fontSize: 12,
              fontWeight: row.shipDeadlineInfo.isPastDue ? 700 : 400,
            }}
          >
            {row.shipByDate}
          </span>
        ) : (
          <span style={{ color: "#8b9296" }}>—</span>
        ),
    },
    { key: "tracking_number", label: "Tracking Number", maxWidth: 130 },
    { key: "shipmentDate", label: "Shipment Date" },
    {
      key: "actions",
      label: "Action",
      align: "center",
      render: (row) => {
        const canUploadReceipt =
          row?.status === "PENDING_SHIPMENT" && row?.shipment_type !== "SHIP_DIRECT";
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <button
              type="button"
              aria-label="view shipment"
              onClick={(e) => {
                e.stopPropagation();
                handleViewShipment(row);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                padding: 0,
                lineHeight: 0,
                boxSizing: "border-box",
                borderRadius: 8,
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#c3c8cc",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Eye size={14} />
            </button>
            {row?.status !== "LABEL_PENDING" && (
              <button
                type="button"
                aria-label="download label"
                disabled={downloadingDoc === "LABEL"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadDoc(row?.id, "LABEL");
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  padding: 0,
                  lineHeight: 0,
                  boxSizing: "border-box",
                  marginLeft: 6,
                  borderRadius: 8,
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: downloadingDoc === "LABEL" ? "rgba(255,255,255,0.3)" : "#c3c8cc",
                  cursor: downloadingDoc === "LABEL" ? "not-allowed" : "pointer",
                  flexShrink: 0,
                }}
              >
                <Download size={13} />
              </button>
            )}
            {canUploadReceipt && (
              <button
                type="button"
                aria-label="upload receipt"
                onClick={(e) => {
                  e.stopPropagation();
                  openUploadDialog(row?.id);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  padding: 0,
                  lineHeight: 0,
                  boxSizing: "border-box",
                  marginLeft: 6,
                  borderRadius: 8,
                  background: "transparent",
                  border: "1px solid #00ADB5",
                  color: "#00ADB5",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <Upload size={13} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Dropdown status checkboxes and the tab row both write to
  // selectedStatuses, so picking one here clears whichever tab was active —
  // otherwise the tab would still look selected for a combo it no longer
  // represents.
  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => {
      const base = activeTab ? [] : prev;
      return checked ? [...base, status] : base.filter((s) => s !== status);
    });
    setActiveTab(null);
  };

  const handleTypeChange = (type, checked) => {
    setSelectedTypes((prev) => (checked ? [...prev, type] : prev.filter((s) => s !== type)));
  };

  const applyFilters = async (overrides = {}) => {
    const statuses = overrides.statuses ?? selectedStatuses;
    const types = overrides.types ?? selectedTypes;
    try {
      setLoadingChunk(true);
      setCurrentPage(0);
      setAllLoadedData([]);
      sessionStorage.setItem("shipTrackingMember_selectedStatuses", JSON.stringify(statuses));
      sessionStorage.setItem("shipTrackingMember_selectedTypes", JSON.stringify(types));
      sessionStorage.setItem("shipTrackingMember_sortDirection", sortDirection);
      const results = await loadDataChunk(0, { statuses, types });
      if (results?.length > 0) {
        setAllLoadedData(results);
      } else {
        setFilteredData([]);
        setAllLoadedData([]);
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      showToast("Error applying filters", "error");
    } finally {
      setLoadingChunk(false);
    }
  };

  const handleFilterOpenChange = (nextOpen) => {
    setFilterOpen(nextOpen);
    if (!nextOpen) {
      applyFilters();
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab.key);
    setSelectedStatuses(tab.statuses);
    applyFilters({ statuses: tab.statuses });
  };

  const handleSortAscending = () => setSortDirection((prev) => prev === "ASC" ? null : "ASC");
  const handleSortDescending = () => setSortDirection((prev) => prev === "DESC" ? null : "DESC");

  const loadInitialData = async (overrides = {}) => {
    const initialChunk = await loadDataChunk(0, overrides);
    setAllLoadedData(initialChunk);
  };

  const checkAndLoadMoreData = async (page, rowsPerPage) => {
    const requiredDataLength = (page + 1) * rowsPerPage;
    const currentDataLength = allLoadedData.length;

    if (currentDataLength >= requiredDataLength) return;
    if (!hasMoreData) return;
    if (loadingChunk) return;
    if (totalRows > 0 && currentDataLength >= totalRows) {
      setHasMoreData(false);
      return;
    }

    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(currentDataLength / CHUNK_SIZE);
      const targetPage = page;
      const newData = await loadDataChunk(nextChunk);
      if (newData && newData?.length > 0) {
        setAllLoadedData((prev) => [...prev, ...newData]);
        setTimeout(() => {
          const startIndex = page * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const combinedData = [...allLoadedData, ...newData];
          setCurrentPage(targetPage);
          setFilteredData(combinedData.slice(startIndex, endIndex));
        }, 100);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error loading more data:", error);
      showToast("Error loading data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangePage = async (newPage) => {
    if (isNavigatingToPage || loadingChunk) return;
    const requiredDataLength = (newPage + 1) * rowsPerPage;

    try {
      setIsNavigatingToPage(true);
      if (allLoadedData.length < requiredDataLength && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        setCurrentPage(newPage);
        setFilteredData(allLoadedData.slice(startIndex, endIndex));
      }
    } catch (error) {
      console.error("Error changing page:", error);
      showToast("Error loading page data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk) return;
    if (!allLoadedData?.length) return;

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pageData = allLoadedData.slice(startIndex, endIndex);
    if (pageData.length > 0) {
      setFilteredData(pageData);
    } else if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [rowsPerPage, allLoadedData]);

  const handleChangeRowsPerPage = async (newValue) => {
    setRowsPerPage(newValue);
    sessionStorage.setItem("shipTrackingMember_rowsPerPage", JSON.stringify(newValue));
    setCurrentPage(0);

    if (allLoadedData.length < newValue && hasMoreData && !loadingChunk) {
      checkAndLoadMoreData(0, newValue);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // Dashboard's "Create shipment" quick action lands here (instead of the
  // old standalone MUI create-shipment page) and asks for the dialog to
  // open immediately via navigation state — clear the state right after so
  // a refresh or back-navigation doesn't reopen it.
  useEffect(() => {
    if (location.state?.openCreateDialog) {
      openCreateDialog();
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (isSearching) return;

    const performSearch = async () => {
      try {
        setIsSearching(true);
        setCurrentPage(0);
        setAllLoadedData([]);
        const results = await loadDataChunk(0);
        if (results?.length > 0) {
          setAllLoadedData(results);
        } else {
          setFilteredData([]);
          setAllLoadedData([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearch]);

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;
    setPageCache(new Map());
    setAllLoadedData([]);
    setHasMoreData(true);

    loadInitialData({ skipCache: true }).then(() => {
      if (currentPageBeforeRefresh > 0) {
        setTimeout(() => setCurrentPage(currentPageBeforeRefresh), 100);
      }
    });
  };

  // Don't double-count a tab's own statuses as "advanced filters" — the tab
  // row already shows which one is active.
  const activeFilterCount = countActiveFiltersAndSort(sortDirection, activeTab ? [] : selectedStatuses, selectedTypes);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Dialog open={uploadDialogOpen} onOpenChange={handleUploadDialogChange}>
        <DialogContent showCloseButton={!uploading}>
          <DialogHeader>
            <DialogTitle>Upload Shipment Receipt</DialogTitle>
            <DialogDescription>
              Upload the shipment label for shipment ID: <b style={{ color: "#fff" }}>{uploadShipmentId}</b>
            </DialogDescription>
          </DialogHeader>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setUploadDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setUploadDragOver(false);
            }}
            onDrop={handleUploadDrop}
            onClick={() => document.getElementById("member-receipt-file-input").click()}
            style={{
              border: `2px dashed ${uploadDragOver ? "#00ADB5" : "rgba(255,255,255,0.15)"}`,
              borderRadius: 12,
              padding: "32px 16px",
              textAlign: "center",
              cursor: "pointer",
              background: uploadDragOver ? "rgba(0,173,181,0.06)" : "transparent",
              transition: "background 0.15s, border-color 0.15s",
            }}
          >
            <UploadCloud size={28} color="#c3c8cc" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>
              Click here or drop a file to upload
            </div>
            <div style={{ fontSize: 11, color: "#c3c8cc", marginTop: 4 }}>
              Accepted file types: .jpg, .png, .pdf
            </div>
            <input
              id="member-receipt-file-input"
              type="file"
              accept=".jpg,.png,.pdf"
              multiple
              style={{ display: "none" }}
              onChange={handleUploadFileInput}
            />
          </div>

          {uploadedFiles.length > 0 && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {uploadedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.02)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <FileText size={14} color="#c3c8cc" style={{ marginRight: 8, flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: 12.5,
                        color: "#c3c8cc",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label="remove file"
                    onClick={() => handleRemoveFile(index)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 24,
                      height: 24,
                      marginLeft: 10,
                      borderRadius: 6,
                      background: "transparent",
                      border: "none",
                      color: "#f27059",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => handleUploadDialogChange(false)} disabled={uploading}>
              Cancel
            </Button>
            <div style={{ marginLeft: 10 }}>
              <Button onClick={handleSubmitReceipt} disabled={uploading || uploadedFiles.length === 0}>
                {uploading ? "Uploading…" : "Submit"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qtyEditOpen} onOpenChange={setQtyEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Quantity</DialogTitle>
            <DialogDescription>
              You can only send up to {qtyEditTarget?.approved_quantity} approved units of{" "}
              {qtyEditTarget?.name}.
            </DialogDescription>
          </DialogHeader>
          <input
            type="text"
            inputMode="numeric"
            value={qtyEditValue}
            onChange={(e) => handleQtyEditChange(e.target.value.replace(/\D/g, ""))}
            style={{
              width: "100%",
              height: 38,
              borderRadius: 8,
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${qtyEditError ? "#f27059" : "rgba(255,255,255,0.12)"}`,
              color: "#fff",
              fontSize: 14,
              padding: "0 12px",
              outline: "none",
            }}
          />
          {qtyEditError && <div style={{ fontSize: 12.5, color: "#f27059", marginTop: 6 }}>{qtyEditError}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQtyEditOpen(false)}>
              Cancel
            </Button>
            <div style={{ marginLeft: 10 }}>
              <Button onClick={confirmQtyEdit} disabled={!!qtyEditError || qtyEditValue === ""}>
                Update
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewDialogOpen} onOpenChange={(open) => !open && closeViewDialog()}>
        <DialogContent showCloseButton={!savingShipment} style={{ maxWidth: 680, maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Shipment {viewShipmentData?.id || ""}</DialogTitle>
            <DialogDescription>Shipping details, boxes, and status.</DialogDescription>
          </DialogHeader>

          {viewLoading ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "#c3c8cc", fontSize: 13 }}>
              Loading…
            </div>
          ) : viewShipmentData ? (
            <div className="member-scroll" style={{ maxHeight: "74vh", overflowY: "auto", paddingRight: 10 }}>
              {(() => {
                const type = viewShipmentData.shipment_type;
                const isShipDirect = type === "SHIP_DIRECT";
                const infoRows = [
                  { label: "Shipping ID", value: viewShipmentData.id },
                  { label: "Tracking Number", value: viewShipmentData.tracking_number },
                  { label: "Walmart Shipping ID", value: viewShipmentData.walmart_shipping_id },
                  { label: "Total Boxes", value: !isShipDirect ? viewShipmentData.total_boxes : null },
                  { label: "Total SKUs", value: !isShipDirect ? viewShipmentData.total_items : null },
                  { label: "Total Quantity", value: viewShipmentData.total_quantity },
                  {
                    label: "Total Weight",
                    value: !isShipDirect
                      ? `${viewShipmentData.total_weight_value ?? ""} ${viewShipmentData.total_weight_unit ?? ""}`.trim()
                      : null,
                  },
                  { label: "Member Name", value: viewShipmentData.member_name },
                  {
                    label: "Creation Date",
                    value: viewShipmentData.created_at
                      ? convertDateFormatInUtc(viewShipmentData.created_at, "MMM D, YYYY")
                      : null,
                  },
                  {
                    label: "Ship from Address",
                    value:
                      !isShipDirect &&
                      formatAddress({
                        address: viewShipmentData.address,
                        building: viewShipmentData.building,
                        city: viewShipmentData.city,
                        state: viewShipmentData.state,
                        zipcode: viewShipmentData.zipcode,
                      }),
                  },
                  { label: "Ship to Address", value: viewShipmentData.ship_to_address },
                  { label: "Phone Number", value: viewShipmentData.user_details?.phone_number },
                  {
                    label: "Shipment Date",
                    value: viewShipmentData.shipment_date
                      ? convertDateFormatInUtc(viewShipmentData.shipment_date, "MMM D, YYYY")
                      : null,
                  },
                  { label: "Shipment Type", value: type?.replace(/_/g, " ") },
                ].filter((row) => row.value);

                const totalBoxPages = Math.ceil((viewShipmentData.box_details?.length || 0) / BOXES_PER_PAGE);
                const boxesForPage = (viewShipmentData.box_details || []).slice(
                  (viewBoxPage - 1) * BOXES_PER_PAGE,
                  viewBoxPage * BOXES_PER_PAGE
                );
                const canEditQuantities = isShipDirect && viewShipmentData.status === "PENDING_SHIPMENT";
                // Pre-Quoted Cooks / In-Store Flips shipments already carry a
                // tracking number from the label-upload step, so bulk tracking
                // import only makes sense for Ship Direct shipments.
                const showBulkTracking = isShipDirect && viewShipmentData.status === "PENDING_SHIPMENT";
                const showUploadReceipt =
                  !viewShipmentData.is_receipt_uploaded &&
                  viewShipmentData.status !== "LABEL_PENDING" &&
                  viewShipmentData.status !== "CANCELLED";
                const showViewTracking =
                  ["SHIPPED", "RECEIVED", "IN_TRANSIT"].includes(viewShipmentData.status) && type !== "WALMART";

                return (
                  <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                      <StatusBadge status={viewShipmentData.status} />
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "18px 20px",
                        marginBottom: 22,
                        padding: 18,
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {infoRows.map((row) => (
                        <div key={row.label}>
                          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", color: "#c3c8cc", marginBottom: 5 }}>
                            {row.label.toUpperCase()}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#c3c8cc" }}>{row.value}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", color: "#c3c8cc", marginBottom: 10 }}>
                      BOXES
                    </div>
                    {boxesForPage.map((box) => (
                      <div
                        key={box.id}
                        style={{
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: 10,
                          padding: 16,
                          marginBottom: 12,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", marginBottom: 10, flexDirection: isMobile ? "column" : "row" }}>
                          <span style={{ fontSize: 14.5, fontWeight: 700, color: "#fff" }}>{box.box_name}</span>
                          <span style={{ fontSize: 12, color: "#c3c8cc" }}>{box.box_items?.length || 0} SKUs</span>
                        </div>
                        {box.box_dimension && (
                          <div style={{ fontSize: 12, color: "#c3c8cc", marginBottom: 12 }}>
                            {!isShipDirect &&
                              `${box.box_dimension.length} × ${box.box_dimension.width} × ${box.box_dimension.height} in · `}
                            {box.box_dimension.weight} lb
                          </div>
                        )}
                        {(box.box_items || []).map((item, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: isMobile ? "flex-start" : "center",
                              padding: "10px 0",
                              borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.05)",
                              flexDirection: isMobile ? "column" : "row",
                            }}
                          >
                            <div style={{ minWidth: 0, marginRight: isMobile ? 0 : 12, marginBottom: isMobile ? 8 : 0, width: isMobile ? "100%" : "auto" }}>
                              <div
                                style={{
                                  fontSize: 13.5,
                                  color: "#c3c8cc",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.name}
                              </div>
                              <div style={{ fontSize: 11.5, color: "#c3c8cc", fontFamily: "ui-monospace, monospace", marginTop: 2 }}>
                                UPC {item.upc} · Order {item.order_id}
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", flexShrink: 0, alignSelf: isMobile ? "flex-end" : "center" }}>
                              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#fff" }}>×{item.quantity}</span>
                              {canEditQuantities && (
                                <button
                                  type="button"
                                  aria-label="edit quantity"
                                  onClick={() => openQtyEdit({ ...item, index }, box.id)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 22,
                                    height: 22,
                                    marginLeft: 6,
                                    borderRadius: 6,
                                    background: "transparent",
                                    border: "none",
                                    color: "#c3c8cc",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FilePenLine size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}

                    {totalBoxPages > 1 && (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 14 }}>
                        <button
                          type="button"
                          disabled={viewBoxPage === 1}
                          onClick={() => setViewBoxPage((p) => p - 1)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 26,
                            height: 26,
                            borderRadius: 7,
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.08)",
                            color: viewBoxPage === 1 ? "rgba(255,255,255,0.25)" : "#fff",
                            cursor: viewBoxPage === 1 ? "not-allowed" : "pointer",
                          }}
                        >
                          <ChevronLeft size={14} />
                        </button>
                        <span style={{ fontSize: 11.5, color: "#c3c8cc", margin: "0 10px" }}>
                          Page {viewBoxPage} of {totalBoxPages}
                        </span>
                        <button
                          type="button"
                          disabled={viewBoxPage === totalBoxPages}
                          onClick={() => setViewBoxPage((p) => p + 1)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 26,
                            height: 26,
                            borderRadius: 7,
                            background: "transparent",
                            border: "1px solid rgba(255,255,255,0.08)",
                            color: viewBoxPage === totalBoxPages ? "rgba(255,255,255,0.25)" : "#fff",
                            cursor: viewBoxPage === totalBoxPages ? "not-allowed" : "pointer",
                          }}
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    )}

                    {canEditQuantities && Object.keys(editedQuantities).length > 0 && (
                      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
                        <Button variant="outline" size="sm" onClick={resetEditedQuantities}>
                          Reset quantities
                        </Button>
                      </div>
                    )}

                    {showBulkTracking && (
                      <div
                        style={{
                          borderTop: "1px solid rgba(255,255,255,0.08)",
                          paddingTop: 16,
                          marginBottom: 16,
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 10 }}>
                          Bulk Tracking Import
                        </div>
                        <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#c3c8cc", marginBottom: 4 }}>
                          SHIP TO ADDRESS
                        </label>
                        <select
                          value={shipToAddress}
                          onChange={(e) => setShipToAddress(e.target.value)}
                          style={{
                            width: "100%",
                            height: 38,
                            borderRadius: 8,
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            color: "#fff",
                            fontSize: 13.5,
                            padding: "0 12px",
                            marginBottom: 12,
                          }}
                        >
                          <option value="" style={{ color: "#000" }}>
                            Select the address
                          </option>
                          {SHIP_TO_ADDRESSES.map((addr) => (
                            <option key={addr} value={addr} style={{ color: "#000" }}>
                              RSC - {addr}
                            </option>
                          ))}
                        </select>

                        <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#c3c8cc", marginBottom: 4 }}>
                          TRACKING NUMBERS
                        </label>
                        <textarea
                          rows={4}
                          value={trackingInput}
                          onChange={(e) => {
                            setTrackingInput(e.target.value);
                            setTrackingInputError("");
                          }}
                          placeholder="Enter tracking IDs separated by commas, spaces, or new lines"
                          style={{
                            width: "100%",
                            borderRadius: 8,
                            background: "rgba(255,255,255,0.04)",
                            border: `1px solid ${trackingInputError ? "#f27059" : "rgba(255,255,255,0.12)"}`,
                            color: "#fff",
                            fontSize: 13.5,
                            padding: 10,
                            outline: "none",
                            resize: "vertical",
                            fontFamily: "inherit",
                          }}
                        />
                        {trackingInputError && (
                          <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>{trackingInputError}</div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                          <Button
                            onClick={handleSaveShipment}
                            disabled={savingShipment || !shipToAddress || !trackingInput.trim()}
                          >
                            {savingShipment ? "Saving…" : "Save"}
                          </Button>
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {showUploadReceipt && (
                        <Button
                          onClick={() => {
                            setViewDialogOpen(false);
                            openUploadDialog(viewShipmentData.id);
                          }}
                        >
                          Upload Receipt
                        </Button>
                      )}
                      {viewShipmentData.is_receipt_uploaded && (
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadDoc(viewShipmentData.id, "RECEIPT")}
                          disabled={downloadingDoc === "RECEIPT"}
                        >
                          <Download size={13} style={{ marginRight: 6 }} />
                          {downloadingDoc === "RECEIPT" ? "Downloading…" : "Download Receipt"}
                        </Button>
                      )}
                      {viewShipmentData.status !== "LABEL_PENDING" && (
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadDoc(viewShipmentData.id, "LABEL")}
                          disabled={downloadingDoc === "LABEL"}
                        >
                          <Download size={13} style={{ marginRight: 6 }} />
                          {downloadingDoc === "LABEL" ? "Downloading…" : "Download Label"}
                        </Button>
                      )}
                      {showViewTracking && (
                        <Button variant="outline" onClick={handleViewTracking}>
                          <Truck size={13} style={{ marginRight: 6 }} />
                          View Tracking
                        </Button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div style={{ padding: "40px 0", textAlign: "center", color: "#c3c8cc", fontSize: 13 }}>
              Shipment not found.
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onOpenChange={(open) => !open && closeCreateDialog()}>
        <DialogContent showCloseButton={!createSubmitting} style={{ maxWidth: 760, maxHeight: "92vh" }}>
          {(() => {
            const isReview = Boolean(totalBox && activeStep === totalBox + 1);
            const boxIndex = activeStep - 1;
            const box = createFormData[boxIndex];
            const boxRuleErrors = getBoxRuleErrors(box?.box_dimension);
            const hasCurrentBoxRuleErrors = Object.keys(boxRuleErrors).length > 0;
            const estimatedShipmentValue = getShipmentEstimatedValue(createFormData);
            const hasCurrentBoxItemLimitErrors = Boolean(
              box?.box_items?.some((item) => item.quantity > getRemainingAllowedQty(item.item_id, boxIndex))
            );
            const currentBoxUsesCopiedDimensions = Boolean(box?.dimensions_copied);
            const nextButtonLabel =
              boxPhase === "items"
                ? currentBoxUsesCopiedDimensions
                  ? activeStep === totalBox
                    ? "Next: Review"
                    : `Next: Box ${activeStep + 1}`
                  : "Next: Dimensions"
                : activeStep === totalBox
                  ? "Next: Review"
                  : `Next: Box ${activeStep + 1}`;

            return (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {activeStep === 0
                      ? "Create Shipment"
                      : isReview
                        ? "Review & Submit"
                        : `Box ${activeStep} of ${totalBox}`}
                  </DialogTitle>
                  <DialogDescription>
                    {activeStep === 0
                      ? "How many boxes are you shipping? You'll set the contents and dimensions for each one."
                      : isReview
                        ? "Confirm everything looks right, then create your shipment."
                        : boxPhase === "items"
                          ? "Select approved items and set how many go in this box."
                          : "Enter this box's dimensions and weight."}
                  </DialogDescription>
                </DialogHeader>

                {totalBox > 0 && activeStep > 0 && (
                  <div style={{ display: "flex", marginBottom: 16 }}>
                    {Array.from({ length: totalBox + 1 }, (_, i) => i + 1).map((n) => (
                      <div
                        key={n}
                        style={{
                          flex: 1,
                          height: 4,
                          borderRadius: 2,
                          marginRight: n <= totalBox ? 4 : 0,
                          background: n < activeStep || (n === activeStep && isReview) ? "#00ADB5" : n === activeStep ? "rgba(0,173,181,0.4)" : "#2a2a2e",
                        }}
                      />
                    ))}
                  </div>
                )}

                {createLoading ? (
                  <div style={{ padding: "40px 0", textAlign: "center", color: "#c3c8cc", fontSize: 14 }}>
                    Loading your approved items…
                  </div>
                ) : (
                  <div
                    className="member-scroll"
                    style={{
                      maxHeight: isReview ? "none" : "66vh",
                      overflowY: isReview ? "visible" : "auto",
                      paddingRight: isReview ? 0 : 10,
                    }}
                  >
                    {activeStep === 0 && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "20px 0" }}>
                        <Package size={36} color="#c3c8cc" style={{ marginBottom: 14 }} />
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <button
                            type="button"
                            onClick={handleCreateReduceBox}
                            disabled={!totalBox || totalBox <= 0}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              background: "#1e1e1e",
                              border: "1px solid #2a2a2e",
                              color: !totalBox || totalBox <= 0 ? "#4a4a4e" : "#fff",
                              fontSize: 18,
                              cursor: !totalBox || totalBox <= 0 ? "not-allowed" : "pointer",
                            }}
                          >
                            −
                          </button>
                          <div style={{ width: 64, textAlign: "center", fontSize: 24, fontWeight: 800, color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                            {totalBox || 0}
                          </div>
                          <button
                            type="button"
                            onClick={handleCreateAddBox}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 8,
                              background: "#1e1e1e",
                              border: "1px solid #2a2a2e",
                              color: "#fff",
                              fontSize: 18,
                              cursor: "pointer",
                            }}
                          >
                            +
                          </button>
                        </div>
                        <div style={{ fontSize: 12.5, color: "#c3c8cc", marginTop: 8 }}>
                          box{totalBox === 1 ? "" : "es"}
                        </div>
                      </div>
                    )}

                    {activeStep >= 1 && !isReview && boxPhase === "items" && box && (
                      <>
                        {currentBoxUsesCopiedDimensions && (
                          <div
                            style={{
                              marginBottom: 14,
                              padding: "10px 12px",
                              borderRadius: 8,
                              border: "1px solid rgba(0,173,181,0.22)",
                              background: "rgba(0,173,181,0.06)",
                              color: "#c3c8cc",
                              fontSize: 12.5,
                              lineHeight: 1.45,
                            }}
                          >
                            Dimensions and weight were applied from the previous box. Choose items and quantities for this box.
                          </div>
                        )}
                        <div style={{ position: "relative", marginBottom: 16 }}>
                          <Search
                            size={14}
                            style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#c3c8cc" }}
                          />
                          <Input
                            value={itemSearchTerm}
                            onChange={(e) => setItemSearchTerm(e.target.value)}
                            placeholder="Search item, UPC, or order ID"
                            style={{ height: 40, fontSize: 13.5, padding: "0 12px 0 36px" }}
                          />
                        </div>
                        {(() => {
                          const selectedIds = box.box_items.map((i) => i.item_id);
                          const query = itemSearchTerm.trim().toLowerCase();
                          // Split into two checks so the empty state can say
                          // *why* nothing shows — "you have nothing approved
                          // to ship" and "your search matched nothing" look
                          // identical to a user otherwise, which reads as
                          // broken even when it's accurately reflecting data.
                          const eligibleOptions = upcOptions.filter((option) => {
                            const remaining = getRemainingAllowedQty(option.item_id, boxIndex);
                            return remaining > 0 || selectedIds.includes(option.item_id);
                          });
                          const visibleOptions = !query
                            ? eligibleOptions
                            : eligibleOptions.filter(
                                (option) =>
                                  option.item_name?.toLowerCase().includes(query) ||
                                  option.upc?.toLowerCase().includes(query) ||
                                  option.order_id?.toLowerCase().includes(query)
                              );

                          if (eligibleOptions.length === 0) {
                            return (
                              <div style={{ padding: "32px 16px", textAlign: "center", color: "#c3c8cc", fontSize: 13.5 }}>
                                You have no approved order items left to ship right now.
                                <div style={{ fontSize: 11.5, marginTop: 6, lineHeight: 1.6 }}>
                                  Items show up here once an order is approved and still has quantity you
                                  haven't shipped yet.
                                </div>
                              </div>
                            );
                          }

                          if (visibleOptions.length === 0) {
                            return (
                              <div style={{ padding: "32px 16px", textAlign: "center", color: "#c3c8cc", fontSize: 13.5 }}>
                                No items match "{itemSearchTerm.trim()}".
                                <div style={{ marginTop: 12 }}>
                                  <Button variant="outline" size="sm" onClick={() => setItemSearchTerm("")}>
                                    Clear search
                                  </Button>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {visibleOptions.map((option) => {
                                const isSelected = selectedIds.includes(option.item_id);
                                const remaining = getRemainingAllowedQty(option.item_id, boxIndex);
                                const selectedItem = box.box_items.find((i) => i.item_id === option.item_id);
                                const overLimit = isSelected && selectedItem?.quantity > remaining;

                                return (
                                  <div
                                    key={option.item_id}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "14px 16px",
                                      borderRadius: 10,
                                      border: `1px solid ${overLimit ? "rgba(242,112,89,0.4)" : isSelected ? "rgba(0,173,181,0.35)" : "rgba(255,255,255,0.08)"}`,
                                      background: isSelected ? "rgba(0,173,181,0.07)" : "rgba(255,255,255,0.015)",
                                      transition: "background 0.12s, border-color 0.12s",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      aria-label={isSelected ? "remove item" : "add item"}
                                      onClick={() => toggleItemSelection(boxIndex, option.item_id)}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 20,
                                        height: 20,
                                        marginRight: 14,
                                        borderRadius: 6,
                                        border: isSelected ? "1px solid #00ADB5" : "1px solid rgba(255,255,255,0.25)",
                                        background: isSelected ? "rgba(0,173,181,0.18)" : "transparent",
                                        color: "#00ADB5",
                                        cursor: "pointer",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isSelected && <Check size={12} />}
                                    </button>
                                    <div
                                      onClick={() => toggleItemSelection(boxIndex, option.item_id)}
                                      style={{ flex: 1, minWidth: 0, cursor: "pointer", marginRight: 16 }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          color: "#f2f4f5",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {option.item_name}
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          flexWrap: "wrap",
                                          marginTop: 5,
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: 11.5,
                                            color: "#c3c8cc",
                                            fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                                            marginRight: 8,
                                          }}
                                        >
                                          UPC {option.upc} · Order {option.order_id}
                                        </span>
                                        <Badge variant={remaining > 0 ? "secondary" : "error"} style={{ fontFamily: "ui-monospace, SF Mono, Menlo, monospace" }}>
                                          {remaining} remaining
                                        </Badge>
                                      </div>
                                      {overLimit && (
                                        <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6, fontWeight: 600 }}>
                                          Exceeds remaining allowed quantity
                                        </div>
                                      )}
                                      {option.priceLockDeadlineInfo && (
                                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
                                          <span style={{ fontSize: 11.5, color: "#c3c8cc" }}>
                                            Create shipment by {option.createShipmentBy}
                                          </span>
                                          <PolicyDeadlineBadge
                                            info={option.priceLockDeadlineInfo}
                                            title="Orders created late may be repriced at current market rates."
                                          />
                                        </div>
                                      )}
                                    </div>
                                    {isSelected && (
                                      <QuantityStepper
                                        value={selectedItem?.quantity ?? 0}
                                        onChange={(next) => handleCreateQuantityChange(boxIndex, option.item_id, next)}
                                        min={0}
                                        max={remaining}
                                        error={overLimit}
                                        size="sm"
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    )}

                    {activeStep >= 1 && !isReview && boxPhase === "dimensions" && box && (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
                          {[
                            ["height", "Height", "in", Ruler],
                            ["width", "Width", "in", Ruler],
                            ["length", "Length", "in", Ruler],
                            ["weight", "Weight", "lb", Weight],
                          ].map(([field, fieldLabel, unit, FieldIcon]) => (
                            <div key={field}>
                              <Label htmlFor={`dim-${field}`} style={{ display: "flex", alignItems: "center" }}>
                                <FieldIcon size={13} style={{ marginRight: 6, color: "#c3c8cc" }} />
                                {fieldLabel}
                              </Label>
                              <div style={{ position: "relative" }}>
                                <Input
                                  id={`dim-${field}`}
                                  type="text"
                                  inputMode="decimal"
                                  value={box.box_dimension[field]}
                                  onChange={(e) => {
                                    // Text input with manual filtering instead of type="number" —
                                    // keeps the browser's native up/down spinner arrows from
                                    // rendering (they crowded the unit label and looked unpolished).
                                    const sanitized = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
                                    handleCreateFormDataChange(boxIndex, field, sanitized);
                                  }}
                                  style={{ height: 42, fontSize: 14, padding: "0 40px 0 12px" }}
                                />
                                <span
                                  style={{
                                    position: "absolute",
                                    right: 12,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    color: "#c3c8cc",
                                    pointerEvents: "none",
                                  }}
                                >
                                  {unit}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div style={{ marginTop: 10, fontSize: 11.5, color: "#8b9296" }}>{SHIPMENT_BOX_LIMIT_HELPER}</div>

                        {hasCurrentBoxRuleErrors && (
                          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                            {boxRuleErrors.weight && <div style={{ fontSize: 11.5, color: "#f27059", fontWeight: 700 }}>{boxRuleErrors.weight}</div>}
                            {boxRuleErrors.dimensions && (
                              <div style={{ fontSize: 11.5, color: "#f27059", fontWeight: 700, lineHeight: 1.45 }}>{boxRuleErrors.dimensions}</div>
                            )}
                          </div>
                        )}

                        {totalBox - activeStep > 0 && (
                          <div
                            style={{
                              marginTop: 22,
                              padding: "14px 16px",
                              borderRadius: 10,
                              border: "1px solid rgba(255,255,255,0.08)",
                              background: "rgba(255,255,255,0.02)",
                            }}
                          >
                            <label style={{ display: "flex", alignItems: "flex-start", fontSize: 13, color: "#c3c8cc", cursor: "pointer" }}>
                              <button
                                type="button"
                                role="checkbox"
                                aria-checked={replicateBoxes}
                                onClick={() => {
                                  setReplicateBoxes((prev) => !prev);
                                  setNumberOfBoxesToReplicate(1);
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 18,
                                  height: 18,
                                  margin: "1px 10px 0 0",
                                  borderRadius: 5,
                                  border: replicateBoxes ? "1px solid #00ADB5" : "1px solid rgba(255,255,255,0.25)",
                                  background: replicateBoxes ? "rgba(0,173,181,0.18)" : "transparent",
                                  color: "#00ADB5",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                {replicateBoxes && <Check size={11} />}
                              </button>
                              <span>
                                Apply these dimensions to upcoming boxes
                                <span style={{ display: "block", fontSize: 11.5, color: "#8b9296", marginTop: 3 }}>
                                  Copies dimensions and weight only. You'll still choose items and quantities for each box.
                                </span>
                              </span>
                            </label>
                            {replicateBoxes && (
                              <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 12.5, color: "#c3c8cc" }}>
                                  Apply to next {numberOfBoxesToReplicate} box{numberOfBoxesToReplicate === 1 ? "" : "es"}
                                </span>
                                <QuantityStepper
                                  value={numberOfBoxesToReplicate}
                                  onChange={handleReplicateBoxCountChange}
                                  min={1}
                                  max={totalBox - activeStep}
                                  size="sm"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {isReview && (
                      <>
                        <ShipmentBoxesReview boxes={createFormData} isMobile={isMobile} />
                        <ShipmentRulesReview
                          shipmentValue={estimatedShipmentValue}
                          acknowledged={rulesAcknowledged}
                          onAcknowledgedChange={setRulesAcknowledged}
                          isMobile={isMobile}
                        />
                      </>
                    )}
                  </div>
                )}

                <DialogFooter>
                  {activeStep > 0 && (
                    <Button variant="outline" onClick={handleWizardBack} disabled={createSubmitting}>
                      Back
                    </Button>
                  )}
                  <div style={{ marginLeft: activeStep > 0 ? 10 : 0 }}>
                    {isReview ? (
                      <Button onClick={handleCreateSubmit} disabled={createSubmitting || !rulesAcknowledged}>
                        {createSubmitting ? "Creating…" : "Create Shipment"}
                      </Button>
                    ) : activeStep === 0 ? (
                      <Button onClick={handleWizardNext} disabled={!totalBox}>
                        Next
                      </Button>
                    ) : (
                      <Button
                        onClick={handleWizardNext}
                        disabled={
                          boxPhase === "items"
                            ? !box?.box_items?.length || box.box_items.some((i) => !(i.quantity > 0)) || hasCurrentBoxItemLimitErrors
                            : !isBoxComplete(boxIndex) || hasCurrentBoxRuleErrors
                        }
                      >
                        {nextButtonLabel}
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Shipping & Tracking" subtitle="Review your submitted shipment details." />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 12,
          alignItems: "stretch",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            border: "1px solid rgba(242, 112, 89, 0.24)",
            background: "rgba(242, 112, 89, 0.08)",
            borderRadius: 12,
            padding: "12px 14px",
          }}
        >
          <AlertTriangle size={18} style={{ color: "#f27059", flexShrink: 0, marginTop: 1 }} />
          <div style={{ color: "#f2f4f5", fontSize: 13, lineHeight: 1.5 }}>
            <strong style={{ color: "#fff" }}>Shipping window:</strong>{" "}
            Once your labels are issued, your shipment status will change to Pending Shipment. Ensure to ship your package(s) within 48h of
            receiving labels. Shipments not scanned in time may be cancelled.
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 10,
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid rgba(0,173,181,0.24)",
            background: "rgba(0,173,181,0.07)",
            color: "#c3c8cc",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <Truck size={16} color="#00ADB5" style={{ marginTop: isMobile ? 2 : 0, flexShrink: 0 }} />
          <span>
            <strong style={{ color: "#fff" }}>Ship Direct:</strong>{" "}
            Shipping from a retailer or with your labels? Use Ship Direct to bulk-upload tracking.
          </span>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <StatCard
          label="NEEDS LABEL"
          value={shipmentStats ? shipmentStats.needsLabel : "—"}
          accent="#e8b84b"
          detail="request to ship"
        />
        <StatCard
          label="IN TRANSIT"
          value={shipmentStats ? shipmentStats.inTransit : "—"}
          accent="#22d3ee"
          detail="on the way"
        />
        <StatCard
          label="RECEIVED"
          value={shipmentStats ? shipmentStats.receivedThisYear : "—"}
          accent="#4caf50"
          detail="this year"
        />
        <StatCard
          label="WEIGHT"
          value={shipmentStats && shipmentStats.lbsWeight > 0 ? `${shipmentStats.lbsWeight} lbs` : "—"}
          accent="#af58f2"
          detail="received, lbs tracked"
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          flexWrap: "wrap",
          marginBottom: 20,
          gap: isMobile ? 10 : 0,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start", marginBottom: 8, width: isMobile ? "100%" : "auto" }}>
          {TAB_OPTIONS.map((tab, index) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabClick(tab)}
                style={{
                  marginLeft: index === 0 ? 0 : 8,
                  marginBottom: 8,
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 700,
                  cursor: "pointer",
                  border: active ? "1px solid rgba(0,173,181,0.4)" : "1px solid #2a2a2e",
                  background: active ? "rgba(0,173,181,0.16)" : "#1e1e1e",
                  color: active ? "#00ADB5" : "#c3c8cc",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", marginBottom: 8, width: isMobile ? "100%" : "auto", gap: isMobile ? 10 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", flex: isMobile ? "1 1 0" : "0 0 auto", minWidth: 0, gap: isMobile ? 8 : 12 }}>
            {!isMobile && <RefreshButton onRefresh={handleRefresh} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setDebouncedTerm={setDebouncedSearch}
              />
            </div>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, flexShrink: 0 }}>
            <DropdownMenu open={filterOpen} onOpenChange={handleFilterOpenChange}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter size={13} style={{ marginRight: 6 }} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="tw-w-64">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {STATUS_OPTIONS.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.includes(status)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(status, checked)}
                  >
                    {status.replace(/_/g, " ")}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                {TYPE_OPTIONS.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={type}
                    checked={selectedTypes.includes(type)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleTypeChange(type, checked)}
                  >
                    {type.replace(/_/g, " ")}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuItem active={sortDirection === "ASC"} onClick={handleSortAscending}>
                  <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                  Sort by Box Qty (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "DESC"} onClick={handleSortDescending}>
                  <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                  Sort by Box Qty (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, width: isMobile ? "100%" : "auto" }}>
            <Button onClick={openCreateDialog} style={{ width: isMobile ? "100%" : "auto" }}>Create New Shipment</Button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) => handleChangePage(newPage)}
        onRowsPerPageChange={(newValue) => handleChangeRowsPerPage(newValue)}
        loading={loadingChunk || isNavigatingToPage}
        minWidth={1120}
      />
    </div>
  );
};

export default MemberShippingAndTrackingPage;
